// ============================================================
// useCapabilities — client consumption of the server-truth
// capability manifest (GET /api/capabilities, packet §05).
//
// Surfaces derive action availability from THIS, never from a
// hard-coded frontend flag. The manifest never fabricates: while
// it is loading (or the caller is signed out) every lookup returns
// `null`, and callers must render an honest "checking…"/default
// state rather than assuming live.
//
// In demo mode (no live runtime) the hook does not fetch — the
// manifest stays null, mirroring usePlatformProviderStatus's
// honest "unknown" posture.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { api, isAuthError } from '../api/client'
import type { Capability, CapabilityManifest } from '../api/client'
import { getRuntime } from './_mode'

export interface UseCapabilitiesResult {
  manifest: CapabilityManifest | null
  loading: boolean
  /** Non-auth failure reaching the manifest; auth-required is not an error. */
  error: string | null
  /** Lookup helper — null while loading/signed-out/unknown key. */
  cap: (key: string) => Capability | null
}

export function useCapabilities(): UseCapabilitiesResult {
  const [manifest, setManifest] = useState<CapabilityManifest | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const { live } = getRuntime()
    if (!live) {
      setLoading(false)
      return
    }
    api.capabilities
      .get()
      .then((m) => {
        if (cancelled) return
        setManifest(m)
        setError(null)
      })
      .catch((err) => {
        if (cancelled) return
        // Signed-out is a normal state, not a manifest fault.
        if (!isAuthError(err)) {
          setError(err instanceof Error ? err.message : String(err))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const cap = useCallback(
    (key: string): Capability | null => manifest?.capabilities?.[key] ?? null,
    [manifest],
  )

  return { manifest, loading, error, cap }
}
